import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Wrench, RefreshCw, BellRing, Plus, Archive } from 'lucide-react';
import { API, STATUS_META, TYPE_LABEL, TypeIcon, fmtMeter, StatusPill } from './fleet/fleetShared';
import FleetUnitModal from './fleet/FleetUnitModal';
import FleetAddUnitModal from './fleet/FleetAddUnitModal';

const TYPE_FILTERS = ['all', 'truck', 'trailer', 'forklift', 'welder', 'other'];

function SummaryChip({ k, label, count, active, onClick }) {
  const m = STATUS_META[k] || STATUS_META.no_plan;
  return (
    <button onClick={onClick} data-testid={`fleet-sum-${k}`}
      className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-colors ${active ? 'bg-white text-slate-900 border-white' : 'bg-slate-900/5 text-slate-900 border-slate-200 hover:bg-slate-900/10'}`}>
      <span className={`w-2.5 h-2.5 rounded-full ${m.dot}`} />
      <span className="font-bold text-base">{count}</span>
      <span className={active ? 'text-slate-600' : 'text-blue-800'}>{label}</span>
    </button>
  );
}

function UnitRow({ e, onOpen }) {
  const meter = fmtMeter(e.meter);
  const overall = e.retired ? STATUS_META.retired : (STATUS_META[e.overall] || STATUS_META.no_plan);
  return (
    <button onClick={() => onOpen(e.id)} data-testid={`fleet-unit-${e.id}`}
      className={`w-full text-left rounded-xl shadow hover:shadow-md border-l-4 transition-shadow p-4 flex flex-col md:flex-row md:items-center gap-3 ${e.retired ? 'bg-slate-100 opacity-75' : 'bg-white'}`}
      style={{ borderLeftColor: e.retired ? '#94a3b8' : e.overall === 'overdue' ? '#ef4444' : e.overall === 'due_soon' ? '#f59e0b' : e.overall === 'ok' ? '#10b981' : '#cbd5e1' }}>
      <div className="flex items-center gap-3 md:w-[300px] shrink-0">
        <div className={`p-2 rounded-lg ${e.overall === 'overdue' ? 'bg-red-50 text-red-600' : e.overall === 'due_soon' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-600'}`}>
          <TypeIcon type={e.equipment_type} />
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 truncate">{e.equipment_name}{e.manual && <span className="ml-1.5 text-[9px] font-semibold text-slate-500 border border-slate-300 rounded px-1 align-middle">Ops</span>}</div>
          <div className="text-xs text-slate-500">
            {TYPE_LABEL[(e.equipment_type || '').toLowerCase()] || e.equipment_type}
            {e.year ? ` · ${e.year}` : ''}{e.license_plate ? ` · ${e.license_plate}` : ''}
            {meter ? ` · ${meter}` : ''}
          </div>
        </div>
      </div>
      <div className="flex-1 flex flex-wrap gap-1.5">
        {e.load_error && <StatusPill status="overdue">Could not evaluate this unit — check its plan items</StatusPill>}
        {e.retired && <StatusPill status="retired">Removed from fleet{e.retired_reason ? ` — ${e.retired_reason}` : ''}</StatusPill>}
        {!e.retired && !e.load_error && e.items.length === 0 && <StatusPill status="no_plan">No maintenance plan yet</StatusPill>}
        {e.items.map(it => (
          <StatusPill key={it.id} status={it.status} testid={`fleet-item-pill-${it.id}`}>
            {it.label}{it.status !== 'no_baseline' ? ` · ${it.summary}` : ''}
          </StatusPill>
        ))}
      </div>
      <div className="shrink-0 text-xs font-semibold" style={{ color: e.overall === 'overdue' ? '#dc2626' : e.overall === 'due_soon' ? '#d97706' : '#64748b' }}>
        {overall.label} →
      </div>
    </button>
  );
}

export default function FleetMaintenance() {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState(null);
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState(null);
  const [pushing, setPushing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [showRetired, setShowRetired] = useState(false);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/overview`, { params: showRetired ? { include_retired: true } : {} }); setData(r.data); }
    catch (e) {
      const status = e.response?.status;
      toast.error(e.response?.data?.detail || (status ? `Failed to load fleet (server error ${status}) — tell the office, the unit list could not be read` : 'Failed to load fleet — check your connection'));
      setData({ equipment: [], summary: {} });
    }
  }, [showRetired]);
  useEffect(() => { load(); axios.get(`${API}/meta`).then(r => setMeta(r.data)).catch(() => {}); }, [load]);

  const sendDigest = async () => {
    setPushing(true);
    try { const r = await axios.post(`${API}/run-alerts`); toast.success(r.data.pushed ? `Digest pushed to ${r.data.sent} device(s)` : 'Nothing due — no push sent'); }
    catch { toast.error('Could not send digest'); }
    setPushing(false);
  };

  const list = (data?.equipment || []).filter(e => {
    if (typeFilter !== 'all' && (e.equipment_type || '').toLowerCase() !== typeFilter) return false;
    if (statusFilter && (e.retired || e.overall !== statusFilter)) return false;
    if (q) { const s = `${e.equipment_name} ${e.id} ${e.license_plate || ''} ${e.make || ''} ${e.model || ''}`.toLowerCase(); if (!s.includes(q.toLowerCase())) return false; }
    return true;
  });
  const s = data?.summary || {};

  return (
    <div className="p-4 md:p-6 min-h-screen" data-testid="fleet-maintenance-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
        <div>
          <div className="flex items-center gap-3"><Wrench className="w-7 h-7 text-slate-900" /><h1 className="text-2xl md:text-3xl font-bold text-slate-900">Fleet Maintenance</h1></div>
          <p className="text-blue-800 text-sm">Oil changes, tires, brakes and service history for every owned unit</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="fleet-refresh" className="p-2 bg-slate-900/5 hover:bg-slate-900/10 rounded-lg text-slate-900"><RefreshCw className="w-4 h-4" /></button>
          {data?.can_edit && (
            <button onClick={sendDigest} disabled={pushing} data-testid="fleet-send-digest"
              className="flex items-center gap-2 px-3 py-2 bg-slate-900/5 hover:bg-slate-900/10 rounded-lg text-slate-900 text-sm disabled:opacity-50">
              <BellRing className="w-4 h-4" /> Push due-items digest
            </button>
          )}
          {data?.can_edit && (
            <button onClick={() => setAdding(true)} data-testid="fleet-add-unit"
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm font-medium">
              <Plus className="w-4 h-4" /> Add vehicle
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-4 mb-4">
        {[['overdue', 'overdue'], ['due_soon', 'due soon'], ['ok', 'up to date'], ['as_needed', 'as needed'], ['no_baseline', 'need baseline'], ['no_plan', 'no plan']].map(([k, label]) => (
          <SummaryChip key={k} k={k} label={label} count={s[k] || 0} active={statusFilter === k} onClick={() => setStatusFilter(statusFilter === k ? null : k)} />
        ))}
        {(s.retired > 0 || showRetired) && (
          <button onClick={() => setShowRetired(v => !v)} data-testid="fleet-toggle-retired"
            className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm ${showRetired ? 'bg-white text-slate-900 border-white' : 'bg-slate-900/5 text-slate-900 border-slate-200 hover:bg-slate-900/10'}`}>
            <Archive className="w-3.5 h-3.5 text-slate-500" /><span className="font-bold text-base">{s.retired || 0}</span><span className={showRetired ? 'text-slate-600' : 'text-blue-800'}>{showRetired ? 'retired shown' : 'retired'}</span>
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search unit, plate, make…" data-testid="fleet-search"
            className="w-full pl-9 pr-3 py-2 rounded-lg text-sm bg-white text-slate-900 outline-none" />
        </div>
        <div className="flex flex-wrap gap-1 bg-slate-900/5 rounded-lg p-1">
          {TYPE_FILTERS.map(t => (
            <button key={t} onClick={() => setTypeFilter(t)} data-testid={`fleet-type-${t}`}
              className={`px-2.5 py-1.5 rounded-md text-sm font-medium ${typeFilter === t ? 'bg-white text-blue-700' : 'text-slate-900 hover:bg-slate-900/5'}`}>
              {t === 'all' ? 'All' : `${TYPE_LABEL[t]}s`}
            </button>
          ))}
        </div>
      </div>

      {!data ? <div className="p-8 text-center text-blue-800">Loading…</div> :
        list.length === 0 ? <div className="p-8 text-center text-blue-800 bg-slate-900/5 rounded-xl" data-testid="fleet-empty">No units match.</div> : (
          <div className="space-y-2">{list.map(e => <UnitRow key={e.id} e={e} onOpen={setOpenId} />)}</div>
        )}

      {openId && <FleetUnitModal eid={openId} meta={meta} onClose={() => setOpenId(null)} onChanged={load} />}
      {adding && <FleetAddUnitModal meta={meta} onClose={() => setAdding(false)} onAdded={(id) => { setAdding(false); load(); setOpenId(id); }} />}
    </div>
  );
}
